import { useState, useCallback, useRef } from 'react';

export interface FingeringNumber {
  mainFingering: number;
  altFingering: number;
  // Stable cross-system id matching the MusicXML `<note id="…">`; rides
  // through the undo/redo queue untouched.
  noteheadId?: string;
}

export interface FingeringGroup {
  fingeringNumList: FingeringNumber[];
  coordinate: {
    x: number;
    y: number;
    upward: boolean;
  };
  noteheadIdList?: string[];
}

// Per-notehead data emitted by the GPU server alongside each fingering group
// page. Optional because pre-Phase-2 projects don't carry it. `pitchStep` is
// the diatonic step index (C=0..B=6); `alter` is the semitone alteration
// (-2 double flat .. +2 double sharp). Ridden through the undo/redo queue
// untouched alongside `fingeringGroupList`.
export interface NoteheadData {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  pitchStep: number;
  alter: number;
  noteheadId: string;
}

export interface ScoreSystemData {
  imageBlobPath: string;
  fingeringGroupList: FingeringGroup[];
  noteheadDataList?: NoteheadData[];
}

interface PdfStateQueueManager {
  stateQueue: ScoreSystemData[][];
  currentStateIndex: number;
  lastSavedStateIndex: number;
  setLastSavedStateIndex: React.Dispatch<React.SetStateAction<number>>;
  addToStateQueue: (state: ScoreSystemData[]) => void;
  handleUndo: () => void;
  handleRedo: () => void;
}

/**
 * Custom hook for managing undo/redo state queue for PDF fingering data
 * @param updateDisplay - Callback function to update the display after undo/redo
 * @returns State queue manager with undo/redo functionality
 */
export const usePdfStateQueue = (
  updateDisplay: (newState: ScoreSystemData[]) => void
): PdfStateQueueManager => {
  const [stateQueue, setStateQueue] = useState<ScoreSystemData[][]>([]);
  const [currentStateIndex, setCurrentStateIndex] = useState<number>(-1);
  const [lastSavedStateIndex, setLastSavedStateIndex] = useState<number>(-1);

  // Use ref to track if we're in the middle of an undo/redo operation
  const isUndoRedoRef = useRef(false);

  // Add the current fingering data to the state queue
  const addToStateQueue = useCallback((state: ScoreSystemData[]) => {
    // Skip if we're in the middle of undo/redo
    if (isUndoRedoRef.current) {
      return;
    }

    setCurrentStateIndex((prevIndex: number) => {
      const newIndex = prevIndex + 1;
      setStateQueue((prevQueue: ScoreSystemData[][]) => {
        const baseQueue = prevQueue.slice(0, newIndex);
        const newState = JSON.parse(JSON.stringify(state));
        const finalQueue = [...baseQueue, newState];

        // If we exceed the limit, remove the oldest state and adjust the index
        if (finalQueue.length > 10) {
          finalQueue.shift();
          setCurrentStateIndex(finalQueue.length - 1);
          // Adjust lastSavedStateIndex when oldest state is removed
          setLastSavedStateIndex((prevSavedIndex) => {
            if (prevSavedIndex === 0) {
              return -1; // Saved state was removed, mark as unsaved
            }
            return prevSavedIndex > 0 ? prevSavedIndex - 1 : prevSavedIndex;
          });
        }

        return finalQueue;
      });
      return newIndex;
    });
  }, []);

  // Handle undo operation
  const handleUndo = () => {
    if (currentStateIndex > 0) {
      isUndoRedoRef.current = true;
      const newIndex = currentStateIndex - 1;
      setCurrentStateIndex(newIndex);

      // Get the previous state and update display
      const previousState = stateQueue[newIndex];
      updateDisplay(JSON.parse(JSON.stringify(previousState)));

      // Reset flag after a brief delay
      setTimeout(() => {
        isUndoRedoRef.current = false;
      }, 0);
    }
  };

  // Handle redo operation
  const handleRedo = () => {
    if (currentStateIndex < stateQueue.length - 1) {
      isUndoRedoRef.current = true;
      const newIndex = currentStateIndex + 1;
      setCurrentStateIndex(newIndex);

      // Get the next state and update display
      const nextState = stateQueue[newIndex];
      updateDisplay(JSON.parse(JSON.stringify(nextState)));

      // Reset flag after a brief delay
      setTimeout(() => {
        isUndoRedoRef.current = false;
      }, 0);
    }
  };

  return {
    stateQueue,
    currentStateIndex,
    lastSavedStateIndex,
    setLastSavedStateIndex,
    addToStateQueue,
    handleUndo,
    handleRedo,
  };
};
